import { useState, useMemo, useEffect } from 'react';
import { useHeaderActions } from '@/contexts/HeaderActionsContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { useFieldConfig } from '@/contexts/FieldConfigContext';
import type { FieldConfig, DurationUnit } from '@/contexts/FieldConfigContext';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { ChevronDown, ArrowLeft, RotateCcw, CheckSquare, Square, Info, Search, X, Eye } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ProjectCreationSetupProps {
  onNavigate?: (page: string) => void;
}

const SECTION_LABELS: Record<FieldConfig['section'], string> = {
  essential: 'Essential Fields',
  general: 'General Information',
  timeline: 'Timeline & Scheduling',
  management: 'Management & Resources',
};

// Field descriptions for tooltips
const FIELD_DESCRIPTIONS: Record<string, string> = {
  projectName: 'The unique name for your project',
  projectCode: 'A short identifier code for the project',
  description: 'Detailed description of the project scope and objectives',
  status: 'Current status of the project (e.g., Planning, Active, Completed)',
  priority: 'Priority level for resource allocation and scheduling',
  type: 'Project type or category for classification',
  location: 'Physical or organizational location of the project',
  startDate: 'When the project is scheduled to begin',
  endDate: 'Target completion date for the project',
  duration: 'Expected length of the project',
  projectOwner: 'Business stakeholder with ultimate accountability and budget approval authority',
  projectManager: 'Primary person responsible for day-to-day project execution and management',
  projectManagers: 'Additional project managers for large projects or sub-teams',
  clients: 'Client(s) or customer(s) for whom the project is being delivered',
  currencyCode: 'Currency for financial tracking and budget reporting',
  budget: 'Financial budget allocated to the project',
  tags: 'Keywords or labels for categorizing and filtering',
};

export function ProjectCreationSetup({ onNavigate }: ProjectCreationSetupProps = {}) {
  const { setHeaderActions, setShowBackButton, setBackButtonPath } = useHeaderActions();
  const {
    fieldConfigs,
    updateFieldConfig,
    timelineSettings,
    updateTimelineSettings,
    hasUnsavedChanges,
    isSaving,
    saveChanges,
    resetChanges,
    resetToDefaults,
    makeAllRequired,
    makeAllOptional,
  } = useFieldConfig();

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    essential: true,
    general: true,
    timeline: true,
    management: true,
  });

  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleToggle = (key: string, checked: boolean) => {
    const config = fieldConfigs.find(c => c.key === key);
    if (config && !config.isAlwaysRequired) {
      updateFieldConfig(config.key, { isRequired: checked });
    }
  };

  const handleSave = async () => {
    try {
      await saveChanges();
      toast.success('Settings saved successfully', {
        description: 'Your project creation setup has been updated.',
      });
    } catch (error) {
      toast.error('Failed to save settings', {
        description: 'Please try again or contact support if the issue persists.',
      });
    }
  };

  const handleReset = () => {
    resetChanges();
    toast.info('Changes discarded', {
      description: 'Reverted to last saved settings.',
    });
  };

  const handleResetToDefaults = () => {
    setShowResetDialog(true);
  };

  const confirmResetToDefaults = () => {
    resetToDefaults();
    setShowResetDialog(false);
    toast.info('Reset to defaults', {
      description: 'All settings have been reset to their default values.',
    });
  };

  const handleMakeAllRequired = () => {
    makeAllRequired();
    toast.info('All fields set to required', {
      description: 'All fields are now marked as required.',
    });
  };

  const handleMakeAllOptional = () => {
    makeAllOptional();
    toast.info('All fields set to optional', {
      description: 'All fields (except essential) are now optional.',
    });
  };

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Filter fields based on search query
  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return fieldConfigs;
    
    const query = searchQuery.toLowerCase();
    return fieldConfigs.filter(field => 
      field.label.toLowerCase().includes(query) ||
      field.key.toLowerCase().includes(query) ||
      FIELD_DESCRIPTIONS[field.key]?.toLowerCase().includes(query)
    );
  }, [fieldConfigs, searchQuery]);

  const groupedFields = filteredFields.reduce((acc, field) => {
    if (!acc[field.section]) {
      acc[field.section] = [];
    }
    acc[field.section].push(field);
    return acc;
  }, {} as Record<FieldConfig['section'], FieldConfig[]>);

  const getRequiredCount = (section: FieldConfig['section']) => {
    const fields = groupedFields[section] || [];
    const requiredCount = fields.filter(f => f.isRequired).length;
    const totalCount = fields.length;
    return { requiredCount, totalCount };
  };

  const hasSearchResults = filteredFields.length > 0;
  const isSearching = searchQuery.trim().length > 0;

  // Set header actions when component mounts or dependencies change
  useEffect(() => {
    // Set back button
    setShowBackButton(true);
    setBackButtonPath('/settings');

    setHeaderActions(
      <>
        {/* Search Bar */}
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search fields..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9 h-9"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchQuery('')}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* Preview Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowPreview(true)}
        >
          <Eye className="h-4 w-4 mr-1.5" />
          Preview Form
        </Button>

        {/* Info Tooltip */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon">
              <Info className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="end" className="max-w-xs">
            <p className="text-sm">
              Fields marked as "Required" must be filled in when creating a new project.
              Essential fields are always required and cannot be changed.
            </p>
          </TooltipContent>
        </Tooltip>
      </>
    );

    // Clean up header actions and back button when component unmounts
    return () => {
      setHeaderActions(null);
      setShowBackButton(false);
      setBackButtonPath(undefined);
    };
  }, [searchQuery, setHeaderActions, setShowBackButton, setBackButtonPath]);

  return (
    <TooltipProvider>
      <div className="h-full flex flex-col">
        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="max-w-3xl mx-auto p-6 space-y-6 pb-32">
            {/* No Results Message */}
            {isSearching && !hasSearchResults && (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No fields found</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  No fields match your search query "{searchQuery}"
                </p>
                <Button variant="outline" size="sm" onClick={() => setSearchQuery('')}>
                  Clear Search
                </Button>
              </div>
            )}

            {/* Sort sections to show essential first */}
            {hasSearchResults && (['essential', 'general', 'timeline', 'management'] as FieldConfig['section'][])
              .filter(section => groupedFields[section])
              .map(section => (
                <Collapsible
                  key={section}
                  open={openSections[section]}
                  onOpenChange={() => toggleSection(section)}
                >
                  <div className="border rounded-lg">
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold">{SECTION_LABELS[section]}</h2>
                            {section === 'essential' && (
                              <span className="text-sm text-muted-foreground font-normal">
                                (Always required)
                              </span>
                            )}
                            {!openSections[section] && section !== 'essential' && (
                              <span className="text-xs text-muted-foreground font-normal">
                                - {getRequiredCount(section).requiredCount}/{getRequiredCount(section).totalCount} required
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronDown
                          className={`h-5 w-5 transition-transform ${
                            openSections[section] ? 'transform rotate-180' : ''
                          }`}
                        />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border-t">
                        {/* Timeline Behavior Settings - only show in timeline section */}
                        {section === 'timeline' && (
                          <div className="p-4 bg-muted/30 border-b space-y-6">
                            <div>
                              <h3 className="text-sm font-semibold mb-1">Timeline Behavior</h3>
                              <p className="text-xs text-muted-foreground">
                                Configure default behaviour for timeline fields
                              </p>
                            </div>

                            {/* Default Duration Unit and Default Mode - Side by Side */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Label htmlFor="default-duration-unit" className="text-sm font-medium">
                                    Default Duration Unit
                                  </Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                      <p>The default unit shown when creating a new project</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                <Select
                                  value={timelineSettings.defaultDurationUnit}
                                  onValueChange={(value: DurationUnit) =>
                                    updateTimelineSettings({ defaultDurationUnit: value })
                                  }
                                >
                                  <SelectTrigger id="default-duration-unit" className="w-full">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="days">Days</SelectItem>
                                    <SelectItem value="weeks">Weeks</SelectItem>
                                    <SelectItem value="months">Months</SelectItem>
                                    <SelectItem value="years">Years</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Label htmlFor="default-mode" className="text-sm font-medium">
                                    Default Mode
                                  </Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                      <p>Whether to default to entering an End Date or a Duration. Users can switch modes when creating projects.</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                <Select
                                  value={timelineSettings.defaultToEndDate ? 'endDate' : 'duration'}
                                  onValueChange={(value: string) =>
                                    updateTimelineSettings({ defaultToEndDate: value === 'endDate' })
                                  }
                                >
                                  <SelectTrigger id="default-mode" className="w-full">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="endDate">End Date</SelectItem>
                                    <SelectItem value="duration">Duration</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {/* Timeline Completion Required */}
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Label htmlFor="timeline-completion-required" className="text-sm font-medium">
                                    Timeline Completion
                                  </Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                      <p className="text-xs">
                                        When set to Required, users must enter Start Date and either End Date or Duration when creating a project. 
                                        When Optional, timeline fields can be left empty.
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">
                                    {timelineSettings.timelineCompletionRequired ? 'Required' : 'Optional'}
                                  </span>
                                  <Switch
                                    id="timeline-completion-required"
                                    checked={timelineSettings.timelineCompletionRequired}
                                    onCheckedChange={(checked) =>
                                      updateTimelineSettings({ timelineCompletionRequired: checked })
                                    }
                                  />
                                </div>
                              </div>
                              {timelineSettings.timelineCompletionRequired && (
                                <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md border">
                                  <p>
                                    <strong>Note:</strong> When Timeline Completion is Required, all timeline fields appear in Quick Create 
                                    and users must provide Start Date + (End Date OR Duration).
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Field Status Information */}
                            <div className="space-y-3 pt-4 border-t">
                              <h4 className="text-sm font-medium text-muted-foreground">Field Status</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center justify-between p-3 bg-muted/20 rounded-md">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">Start Date</span>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent side="right" className="max-w-xs">
                                        <p className="text-xs">
                                          Start Date is the anchor for timeline calculations. It's automatically Required when 
                                          Timeline Completion is Required, and Optional otherwise.
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                  <span className="text-muted-foreground">
                                    {timelineSettings.timelineCompletionRequired ? 'Required' : 'Optional'}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted/20 rounded-md">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">End Date</span>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent side="right" className="max-w-xs">
                                        <p className="text-xs">
                                          End Date is always Optional. Users can choose to enter either End Date or Duration 
                                          when creating a project.
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                  <span className="text-muted-foreground">Always Optional</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted/20 rounded-md">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">Duration</span>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent side="right" className="max-w-xs">
                                        <p className="text-xs">
                                          Duration is always Optional. Users can choose to enter either Duration or End Date 
                                          when creating a project.
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                  <span className="text-muted-foreground">Always Optional</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Field List */}
                        <div className="divide-y">
                          {groupedFields[section]
                            .filter(field => {
                              // For timeline section, exclude startDate, endDate, duration as they're shown in Field Status
                              if (section === 'timeline') {
                                return !['startDate', 'endDate', 'duration'].includes(field.key);
                              }
                              return true;
                            })
                            .sort((a, b) => a.order - b.order)
                            .map(field => (
                              <div key={field.key}>
                                <div className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{field.label}</span>
                                    {FIELD_DESCRIPTIONS[field.key] && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent side="right" className="max-w-xs">
                                          <p className="text-xs">{FIELD_DESCRIPTIONS[field.key]}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">
                                      {field.isRequired ? 'Required' : 'Optional'}
                                    </span>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div>
                                          <Switch
                                            checked={field.isRequired}
                                            onCheckedChange={(checked) => handleToggle(field.key, checked)}
                                            disabled={field.isAlwaysRequired}
                                          />
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>
                                          {field.isAlwaysRequired
                                            ? 'This field is always required and cannot be changed'
                                            : field.isRequired
                                            ? 'Click to make this field optional'
                                            : 'Click to make this field required'}
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                </div>

                              </div>
                            ))}
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
          </div>
        </div>


      </div>

      {/* Reset to Defaults Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Defaults?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset all field requirements and timeline settings to their default values. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmResetToDefaults}>
              Reset to Defaults
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Project Creation Form Preview</DialogTitle>
            <DialogDescription>
              This is how the project creation form will appear with your current settings.
              Required fields are marked with an asterisk (*).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Group fields by section for preview */}
            {(['essential', 'general', 'timeline', 'management'] as FieldConfig['section'][])
              .filter(section => {
                const sectionFields = fieldConfigs.filter(f => f.section === section);
                return sectionFields.length > 0;
              })
              .map(section => {
                const sectionFields = fieldConfigs
                  .filter(f => f.section === section)
                  .sort((a, b) => a.order - b.order);

                return (
                  <div key={section} className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      {SECTION_LABELS[section]}
                    </h3>
                    <div className="space-y-4 pl-4 border-l-2 border-muted">
                      {sectionFields.map(field => (
                        <div key={field.key} className="space-y-2">
                          <Label htmlFor={`preview-${field.key}`} className="text-sm font-medium">
                            {field.label}
                            {field.isRequired && (
                              <span className="text-destructive ml-1">*</span>
                            )}
                          </Label>
                          {/* Show appropriate input type based on field */}
                          {field.key === 'description' ? (
                            <textarea
                              id={`preview-${field.key}`}
                              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              placeholder={`Enter ${field.label.toLowerCase()}...`}
                              disabled
                            />
                          ) : field.key === 'startDate' || field.key === 'endDate' ? (
                            <Input
                              id={`preview-${field.key}`}
                              type="date"
                              placeholder={`Select ${field.label.toLowerCase()}...`}
                              disabled
                            />
                          ) : field.key === 'duration' ? (
                            <div className="flex gap-2">
                              <Input
                                id={`preview-${field.key}`}
                                type="number"
                                placeholder="Enter duration..."
                                className="flex-1"
                                disabled
                              />
                              <Select disabled>
                                <SelectTrigger className="w-32">
                                  <SelectValue placeholder={timelineSettings.defaultDurationUnit} />
                                </SelectTrigger>
                              </Select>
                            </div>
                          ) : field.key === 'status' || field.key === 'priority' || field.key === 'type' ? (
                            <Select disabled>
                              <SelectTrigger id={`preview-${field.key}`}>
                                <SelectValue placeholder={`Select ${field.label.toLowerCase()}...`} />
                              </SelectTrigger>
                            </Select>
                          ) : field.key === 'budget' ? (
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                                $
                              </span>
                              <Input
                                id={`preview-${field.key}`}
                                type="number"
                                placeholder="0.00"
                                className="pl-7"
                                disabled
                              />
                            </div>
                          ) : (
                            <Input
                              id={`preview-${field.key}`}
                              type="text"
                              placeholder={`Enter ${field.label.toLowerCase()}...`}
                              disabled
                            />
                          )}
                          {FIELD_DESCRIPTIONS[field.key] && (
                            <p className="text-xs text-muted-foreground">
                              {FIELD_DESCRIPTIONS[field.key]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

            {/* Timeline Mode Info */}
            {timelineSettings && (
              <div className="mt-6 p-4 bg-muted/50 rounded-lg border">
                <h4 className="text-sm font-semibold mb-2">Timeline Settings</h4>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p>
                    <span className="font-medium">Default Mode:</span>{' '}
                    {timelineSettings.defaultToEndDate ? 'End Date' : 'Duration'}
                  </p>
                  <p>
                    <span className="font-medium">Default Duration Unit:</span>{' '}
                    {timelineSettings.defaultDurationUnit.charAt(0).toUpperCase() + 
                     timelineSettings.defaultDurationUnit.slice(1)}
                  </p>
                  <p>
                    <span className="font-medium">Timeline Completion:</span>{' '}
                    {timelineSettings.timelineCompletionRequired ? 'Required' : 'Optional'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Mode switching is always enabled - users can toggle between End Date and Duration
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close Preview
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
